import React, { memo, useCallback, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import qs from 'querystring';

import { Link, Section } from '@iris/react-components';
import clsx from 'clsx';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { Heading } from 'reaxl';
import { clickTypes, DelayedImpressionObserver, sendClick, sendImpressions } from 'reaxl-analytics';
import { inventorySimilarListingsImpression } from 'reaxl-analytics-handlers';
import { InventoryCarousel } from 'reaxl-organisms';

import { getPriceLabel } from 'wallet-manager';

import { getDealerDetailUrl } from '@/utilities/ddpUrlBuilder';
import { saveVDPCarouselListing } from '@/utilities/saveListing';

import { compareListingsDrawerClick } from '@/analytics/inventoryAnalyticsHandlers';
import { inventoryClick, inventoryImpressions } from '@/analytics/srpAnalyticsHandlers';

import compareListingsDuck from '@/ducks/compareListingsDuck.js';
import queryDuck from '@/ducks/queryDuck.js';
import savedInventoryDuck from '@/ducks/savedInventoryDuck.js';
import userPreferencesDuck from '@/ducks/userPreferencesDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';
import vdpSimilarDealerListingsDuck from '@/ducks/vdp/vdpSimilarDealerListingsDuck.js';
import vdpSimilarListingsDuck from '@/ducks/vdp/vdpSimilarListingsDuck.js';

import useSimilarListings from '@/hooks/useSimilarListings';
import useVdpNavigation from '@/hooks/useVdpNavigation';

import OwnerDetails from '@/components/vehicledetails/OwnerDetails';

/**
 * Container component for displaying similar inventory carousels on the Vehicle Detail Page (VDP).
 * Renders either similar inventory across all listings or similar inventory from the same dealer
 * based on the `isSimilarDealerInventoryOnly` prop.
 *
 * @param {boolean} isSimilarDealerInventoryOnly - Flag to indicate whether to show similar inventory from same dealer only - defaults to `false`.
 *
 * @returns The rendered component or `null`
 */
function SimilarInventoryContainer({ isSimilarDealerInventoryOnly = false }) {
    const {
        brand: [, { channel, inventory_image_url: inventoryImageUrl }],
        personalization: [isSaveEnabled, { listings_saved: authenticatedSavesOnly }],
        similar_inventory: [, {
            override_conquestable: overrideConquestableEnabled,
            eligible_for_non_dealer_inventory: eligibleForNonDealerInventory,
        }],
        ddp_link: [isDdpLinkEnabled, { ddp_base_path: ddpBasePath }],
        enable_new_dealer_card_vdp: [isNewDealerCardVdpEnabled],
    } = useFeatures([
        'brand',
        'personalization',
        'similar_inventory',
        'ddp_link',
        'enable_new_dealer_card_vdp',
    ]);

    const dispatch = useDispatch();
    const navigateToVdp = useVdpNavigation();
    const device = useDevice();
    const isXs = device?.is?.xs;

    const similarListingsDuck = isSimilarDealerInventoryOnly
        ? vdpSimilarListingsDuck
        : vdpSimilarDealerListingsDuck;

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);

    const processedListing = overrideConquestableEnabled
        ? { ...listing, isConquestable: false }
        : listing;

    const { isConquestable, makeCode, owner = {}, priority = '', type } = processedListing;

    // Determine price label based on vehicle make and type
    const priceLabel = getPriceLabel({ makeCode, type });
    const isPrivateSeller = owner?.privateSeller || false;

    const savedListingIds = useSelector(savedInventoryDuck.selectors.getSavedInventoryIds);
    const compareListingIds = useSelector(compareListingsDuck.selectors.getActiveListingIds);
    const compareToggle = useSelector(userPreferencesDuck.selectors.getCompareToggle);
    const query = useSelector(queryDuck.selectors.getDuckState);
    const { sellerType } = query;

    /**
     * Tracks the visible inventory listings in the carousel on the first load.
     * It contains:
     * - `startIndexInCells` - The index of the first visible item in the carousel.
     * - `ids` - An array of listing IDs for the visible items.
     */
    const [selectedInventories, setSelectedInventories] = useState({});

    const applySecondVehicleCarousel = !['PREMIUM', 'FEATURED'].includes(priority);
    const isSimilarListingsCarouselEnabled = applySecondVehicleCarousel && eligibleForNonDealerInventory;
    const hasPrivateSellerTypeParam = sellerType === 'p';

    const ddpLink = _get(getDealerDetailUrl(owner, ddpBasePath), 'href', '');

    const {
        similarListings: transformedSimilarListings,
        isSimilarListingsInitialLoad,
    } = useSimilarListings({
        listing: processedListing,
        channel,
        isSimilarDealerInventoryOnly,
        isSimilarListingsCarouselEnabled,
        similarListingsDuck,
        inventoryImageUrl,
        isConquestable,
        hasPrivateSellerTypeParam,
    });

    const handleSendImpressions = useCallback((index, selectedListingsIds, startingIndex) => {
        const cmp = isSimilarDealerInventoryOnly ? 'rv_lstg_' : 'sim_v_lstg_';
        const listingType = isSimilarDealerInventoryOnly ? 'recommendations' : 'similar';

        sendImpressions({
            name: inventoryImpressions,
            data: {
                cmp,
                selectedItemIds: selectedListingsIds,
                pixallData: {
                    impressionType: listingType,
                    label: `${cmp}${index}`,
                    listingType,
                },
                startingIndex,
            },
        });
    }, [isSimilarDealerInventoryOnly]);

    const handleInViewImpression = useCallback(() => {
        const startCellsIndex = selectedInventories?.startIndexInCells;
        const selectedListingsIds = selectedInventories?.ids || [];

        if (!isSimilarDealerInventoryOnly) {
            handleSendImpressions(startCellsIndex, selectedListingsIds, selectedListingsIds.length * startCellsIndex);
        } else if (selectedListingsIds.length >= 0) {
            sendImpressions({
                name: inventorySimilarListingsImpression,
                data: {
                    selectedItemIds: selectedListingsIds,
                },
            });
        }
    }, [handleSendImpressions, isSimilarDealerInventoryOnly, selectedInventories]);

    const handleInventoryListingClick = useCallback(async (event, selectedListing) => {
        // need to stop propagation and prevent default to allow anchors within listing for SEO purposes
        event.stopPropagation();
        event.preventDefault();
        const targetClassName = event && event.target && event.target.className || [];
        const isSaveClick = targetClassName.includes('glyphicon-heart-o');
        const isSelectClick = targetClassName.includes('input-label') && event.target.innerHTML === 'Select';

        if (!(isSaveClick || isSelectClick) && selectedListing) {
            // Wait for sendClick to complete its internal processing
            // This ensures the analytics event is fully processed client-side
            await sendClick(inventoryClick, event, {
                inventoryId: selectedListing.id,
                par: isSimilarDealerInventoryOnly ? 'rv_lstg' : 'sim_v_lstg',
                pixallData: {
                    eventSource: 'vehicle',
                },
            });

            if (selectedListing.id) {
                const urlParams = qs.parse(window.location.search.substring(1));
                delete urlParams.listingId;
                delete urlParams.clickType;

                dispatch(similarListingsDuck.creators.clear());

                // Wait for 100ms to ensure analytics inventoryClick events have enough time to process
                setTimeout(() => {
                    navigateToVdp({
                        ...urlParams,
                        listingId: selectedListing.id,
                        clickType: isSimilarDealerInventoryOnly ? clickTypes.RECOMMENDATIONS : clickTypes.SIMILAR_INVENTORY,
                    });
                }, 100);
            } else {
                return false;
            }
        }

        return true;
    }, [dispatch, isSimilarDealerInventoryOnly, navigateToVdp, similarListingsDuck.creators]);

    const handleSettle = useCallback((index, _direction, _width, _selectedCells, selectedIndices) => {
        const selectedListings = transformedSimilarListings.slice(selectedIndices[0], selectedIndices[selectedIndices.length - 1] + 1);
        const selectedListingsIds = selectedListings.map(({ id }) => id);
        handleSendImpressions(index, selectedListingsIds, selectedIndices[0]);
    }, [handleSendImpressions, transformedSimilarListings]);

    /**
     * Handler called when the carousel is initially rendered.
     * It captures the listings that are visible on the first load to send view impressions for them.
     *
     * @param {number} startIndexInCells - The index of the first cell to be rendered in the viewport.
     * @param {Array} selectedCells - Array containing all inventories in carousel.
     */
    const handleReady = useCallback((startIndexInCells, selectedCells) => {
        const selectedListingIds = transformedSimilarListings
            .slice(startIndexInCells, (startIndexInCells + selectedCells.length))
            .map(({ id }) => id);

        setSelectedInventories({
            startIndexInCells,
            ids: selectedListingIds,
        });
    }, [transformedSimilarListings]);

    const getCarouselHeading = useCallback(() => {
        let headingText = '';

        if (isSimilarDealerInventoryOnly || isPrivateSeller) {
            headingText = 'Close matches in your area';
        } else if ((applySecondVehicleCarousel && !isSimilarDealerInventoryOnly) || !isConquestable) {
            headingText = 'Check out similar styles from this dealer';
        } else {
            headingText = 'You might also like these similar vehicles';
        }

        // Determine if we should show the link based on the heading text
        const shouldShowLink = isNewDealerCardVdpEnabled && isDdpLinkEnabled && ddpLink && (headingText === 'Check out similar styles from this dealer');

        return (
            <div className="display-flex margin-bottom-2">
                <Heading
                    level={3}
                >
                    {shouldShowLink ? (
                        <a
                            data-cmp="ddpLink"
                            href={ddpLink}
                            className="text-inherit link-undecorated"
                        >
                            {headingText}
                        </a>
                    ) : headingText}
                </Heading>
            </div>
        );
    }, [isConquestable, applySecondVehicleCarousel, isPrivateSeller, isSimilarDealerInventoryOnly, isDdpLinkEnabled, ddpLink, isNewDealerCardVdpEnabled]);

    const showSave = priority !== 'FREE' && (isSaveEnabled || authenticatedSavesOnly);
    const onSaveToggle = useCallback((event, savedListing, wasSaved) => {
        dispatch(saveVDPCarouselListing({
            event,
            listing: savedListing,
            wasSaved,
            sendClick,
            ...(isSimilarDealerInventoryOnly && { parentId: 'rv_lstg' }),
        }));
    }, [dispatch, isSimilarDealerInventoryOnly]);

    const showCompareButton = compareToggle && compareListingIds?.length < 5;
    const isCompared = useCallback((compareListing) => compareListingIds && compareListingIds.includes(compareListing?.id), [compareListingIds]);
    const handleCompareListingsClick = useCallback((compareListing) => async (event) => {
        sendClick(compareListingsDrawerClick, event, {
            clickType: clickTypes.SIMILAR_INVENTORY,
            inventoryId: compareListing?.id,
            inheritPageData: false,
            par: 'comp_v_add',
        });

        dispatch(userPreferencesDuck.creators.setKeys({ showDrawer: true }));
        if (isCompared(compareListing)) {
            dispatch(compareListingsDuck.creators.removeFromCompare(compareListing?.id));
        } else {
            dispatch(compareListingsDuck.creators.addToCompare(compareListing?.id));
        }
    }, [dispatch, isCompared]);

    if (isSimilarListingsInitialLoad) {
        const placeholderConfig = {
            id: isSimilarDealerInventoryOnly ? 'placeholder-similar-inv' : 'placeholder-similar-dealer-inv',
            height: isXs ? 336.5 : 369.5,
        };

        return (
            <div
                id={placeholderConfig.id}
                style={{ height: placeholderConfig.height }}
            />
        );
    }

    if (!transformedSimilarListings?.length
        || (isSimilarListingsCarouselEnabled && isConquestable && !isSimilarDealerInventoryOnly)) {
        return null;
    }

    const headingComponent = getCarouselHeading();
    const dataCmp = isSimilarDealerInventoryOnly ? 'section-similar-inventory' : 'section-similar-dealer-inventory';

    const renderOwnerDetails = !isPrivateSeller && !isConquestable && !isSimilarDealerInventoryOnly && isNewDealerCardVdpEnabled;

    return (
        <DelayedImpressionObserver
            onChange={handleInViewImpression}
            key={dataCmp}
        >
            <Section
                alignment="left"
                className={clsx('iris:mx-0! iris:my-6', { 'iris:mt-8': isXs && !isPrivateSeller, 'iris:mt-0': isPrivateSeller })}
                data-cmp={dataCmp}
            >
                {headingComponent}
                <div className={clsx(
                    renderOwnerDetails && [
                        'similar-inventory-container',
                        isXs && 'overflow-hidden',
                    ]
                )}
                >
                    {renderOwnerDetails && (
                        <div className={clsx('similar-inventory-owner-details', { 'padding-bottom-0': !isDdpLinkEnabled })}>
                            <OwnerDetails uiContext="dealerCard" />
                        </div>
                    )}
                    <div className={clsx(
                        'similar-inventory-carousel',
                        { 'align-self-center': renderOwnerDetails }
                    )}
                    >
                        <InventoryCarousel
                            orientation={device?.is?.xs ? 'portrait' : 'landscape'}
                            listings={transformedSimilarListings}
                            clickType={isSimilarDealerInventoryOnly ? clickTypes.RECOMMENDATIONS : clickTypes.SIMILAR_INVENTORY}
                            carouselSettings={{
                                fullWidthScroll: true,
                                onReady: handleReady,
                                visibilityThreshold: 0.85,
                                fullWidthScrollXs: true,
                            }}
                            owner={owner}
                            onInventoryListingClick={handleInventoryListingClick}
                            onSaveToggle={showSave && onSaveToggle}
                            onSettle={handleSettle}
                            savedListingIds={savedListingIds}
                            showFooter={isConquestable}
                            showCompareButton={showCompareButton}
                            isCompared={isCompared}
                            onCompareClick={handleCompareListingsClick}
                            priceLabel={priceLabel}
                            listingClassName="margin-bottom-0"
                        />
                        { isDdpLinkEnabled && renderOwnerDetails && (
                            <div className="similar-inventory-view-all-link">
                                <Link
                                    aria-label="View all cars at this dealership"
                                    href={ddpLink}
                                    iconPosition="left"
                                    size="md"
                                    variant="standalone"
                                    weight="regular"
                                >
                                    View all cars at this dealership
                                </Link>
                            </div>
                        )}
                    </div>
                </div>
            </Section>
        </DelayedImpressionObserver>
    );
}

SimilarInventoryContainer.propTypes = {
    /**
     * Flag to show similar inventory from same dealer only - defaults to false
     */
    isSimilarDealerInventoryOnly: PropTypes.bool,
};

export default memo(SimilarInventoryContainer, _isEqual);
